/**
 * Shared Types for UnifiedChat components
 */

import { ChatMessage } from '../../services/llmChatService';
import type { Plan } from '../../stores/subscriptionStore';

/**
 * Task Handler Response Type
 *
 * Mirrors the backend TaskOutputType enum. This tells the frontend
 * how to render the response from each task handler.
 *
 * The MessageBubble component uses this to route to the appropriate
 * rendering component (ChartMessageCard, LLMResponseMessageCard, etc.)
 *
 * This is progressively built as tasks complete - each task_complete
 * event includes the outputType from the handler.
 */
export enum TaskHandlerResponseType {
  // Text/Content types - rendered as markdown
  MARKDOWN = 'markdown',
  TEXT = 'text',

  // Structured data types
  JSON = 'json',
  TABLE = 'table',

  // Visual types - rendered with specialized components
  CHART = 'chart',

  // File/Download types
  FILE = 'file',

  // Meta types
  TASK_LIST = 'task_list',
  ERROR = 'error',
  COMPOSITE = 'composite',
}

/**
 * Map TaskHandlerResponseType to message type for rendering
 * This bridges the backend output type to frontend message type
 */
export const mapResponseTypeToMessageType = (
  responseType: TaskHandlerResponseType | string | undefined
): ChatMessage['type'] => {
  switch (responseType) {
    case TaskHandlerResponseType.CHART:
    case 'chart':
      return 'chart';
    case TaskHandlerResponseType.ERROR:
    case 'error':
      return 'llm_response'; // Errors rendered as markdown with error styling
    case TaskHandlerResponseType.MARKDOWN:
    case TaskHandlerResponseType.TEXT:
    case 'markdown':
    case 'text':
    default:
      return 'llm_response';
  }
};

/**
 * Uploaded file with extracted content
 */
export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  content: string;
  timestamp: string;
}

/**
 * File attachment metadata (without content)
 */
export interface FileAttachment {
  name: string;
  size: number;
  type: string;
}

/**
 * Task step for streaming progress display
 */
export interface TaskStep {
  step: number;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error' | 'skipped';
  type?: string;
}

/**
 * Chat session from store
 */
export interface ChatSession {
  sessionId: string;
  title: string;
  messages: ChatMessage[];
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Model tier — user-facing abstraction over raw model IDs.
 *
 * Users choose a tier (Smart / Pro / Expert) rather than a specific model.
 * The tier→model mapping is defined in MODEL_TIERS below.
 */
export type ModelTier = 'smart' | 'pro' | 'expert';

export interface ModelTierConfig {
  tier: ModelTier;
  label: string;
  description: string;
  internalModel: string;
  guestAllowed: boolean;
  /** Minimum subscription plan this mode requires. Enforced server-side
   *  (usage_service.check_model_access); the selector gates the UI to match.
   *  Smart → free, Pro → signals, Expert → strategist. */
  requiredPlan: Plan;
  /** IA Credits consumed per query with this tier. */
  creditCost: number;
}

export const MODEL_TIERS: ModelTierConfig[] = [
  // creditCost is FLAT 1 across tiers (ROADMAP Epic 0.8): every question counts
  // the same. The daily balance is a plain "N questions/day" cap, kept in sync
  // with the backend.
  //
  // The tiers do NOT differ by model (ROADMAP "The AI Assistant…" item 4e).
  // All three run one SKU — the owner moved the paid tiers onto it on
  // 2026-09-06 — so `internalModel` is the same everywhere, and this file used
  // to claim otherwise. What actually differs, and what each description is
  // therefore allowed to promise, is what the server can prove it did:
  //
  //   time on the answer   90s  /  180s  /  300s
  //   knowledge per turn     8  /   12   /   16   principles
  //   checks before you see it  0  /  2  /  3     passes
  //
  // A description that implies a smarter model is a claim nothing backs.
  {
    tier: 'smart',
    label: 'Smart',
    description: 'One pass, quick answers',
    internalModel: 'gemini-3.1-flash-lite',
    guestAllowed: true,
    requiredPlan: 'free',
    creditCost: 1,
  },
  {
    tier: 'pro',
    label: 'Pro',
    description: 'More time, more context, and a second look at the answer',
    internalModel: 'gemini-3.1-flash-lite',
    guestAllowed: false,
    requiredPlan: 'signals',
    creditCost: 1,
  },
  {
    tier: 'expert',
    label: 'Expert',
    description: 'The most time and context, and three checks before you see it',
    internalModel: 'gemini-3.1-flash-lite',
    guestAllowed: false,
    requiredPlan: 'strategist',
    creditCost: 1,
  },
];

/** Daily question allowance for free signed-in users (Epic 0.8). */
export const FREE_DAILY_CREDITS = 150;

export const DEFAULT_TIER: ModelTier = 'smart';

/** Map a tier to its internal model string for API requests. */
export function getTierModel(tier: ModelTier): string {
  return MODEL_TIERS.find(t => t.tier === tier)?.internalModel ?? 'gemini-3.1-flash-lite';
}

/** Look up tier config by tier value. */
export function getTierConfig(tier: ModelTier): ModelTierConfig | undefined {
  return MODEL_TIERS.find(t => t.tier === tier);
}

/**
 * Guest daily query limit (localStorage-enforced)
 */
export const GUEST_DAILY_LIMIT = 20;
export const GUEST_USAGE_STORAGE_KEY = 'ia_guest_queries';

/**
 * Get guest usage from localStorage
 */
export function getGuestUsage(): { count: number; remaining: number } {
  try {
    const data = JSON.parse(localStorage.getItem(GUEST_USAGE_STORAGE_KEY) || '{}');
    const today = new Date().toISOString().split('T')[0];
    if (data.date !== today) return { count: 0, remaining: GUEST_DAILY_LIMIT };
    return { count: data.count || 0, remaining: GUEST_DAILY_LIMIT - (data.count || 0) };
  } catch {
    return { count: 0, remaining: GUEST_DAILY_LIMIT };
  }
}

/**
 * Increment guest usage counter in localStorage
 */
export function incrementGuestUsage(): void {
  const today = new Date().toISOString().split('T')[0];
  try {
    const data = JSON.parse(localStorage.getItem(GUEST_USAGE_STORAGE_KEY) || '{}');
    if (data.date !== today) {
      localStorage.setItem(GUEST_USAGE_STORAGE_KEY, JSON.stringify({ date: today, count: 1 }));
    } else {
      localStorage.setItem(GUEST_USAGE_STORAGE_KEY, JSON.stringify({ date: today, count: (data.count || 0) + 1 }));
    }
  } catch {
    localStorage.setItem(GUEST_USAGE_STORAGE_KEY, JSON.stringify({ date: today, count: 1 }));
  }
}

/**
 * Layout constants
 */
export const SIDEBAR_WIDTH = 280;
export const COLLAPSED_SIDEBAR_WIDTH = 48;
